import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

// Premium, celebratory full-screen takeover shown once the admin reveals the winner.
export function WinnerReveal({ winner, prizeText, onDismiss }) {
  const { t } = useLang();
  const [dismissable, setDismissable] = useState(false);

  useEffect(() => {
    // let the reveal breathe (~3.5s) before allowing dismissal
    const id = setTimeout(() => setDismissable(true), 3500);
    return () => clearTimeout(id);
  }, []);

  if (!winner) return null;

  return (
    <div
      data-testid="winner-reveal-takeover"
      onClick={() => dismissable && onDismiss?.()}
      className="fixed inset-0 z-[100] flex items-center justify-center px-6 overflow-hidden"
      style={{ background: 'radial-gradient(circle at 50% 30%, #16224a 0%, #0B132B 45%, #070C1E 100%)' }}
    >
      {/* soft gold aura */}
      <div className="absolute w-[520px] h-[520px] rounded-full opacity-40 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.35), transparent 65%)' }} />

      <div className="relative text-center max-w-sm w-full reveal-pop">
        <div className="text-7xl trophy-glow mx-auto" role="img" aria-label="trophy">🏆</div>

        <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#E5C158] mt-6 reveal-rise" style={{ animationDelay: '0.3s' }}>
          {t('challengeWinner')}
        </p>

        <h1 className="font-serif font-bold text-5xl mt-3 gold-sheen reveal-rise" style={{ animationDelay: '0.6s' }} data-testid="winner-reveal-name">
          {winner.name}
        </h1>

        <div className="mt-4 inline-flex items-center gap-3 reveal-rise" style={{ animationDelay: '0.9s' }}>
          <span className="font-serif text-3xl text-[#FDFBF7]" data-testid="winner-reveal-score">{winner.score} / {winner.total}</span>
          <span className="text-slate-500">·</span>
          <span className="font-mono text-lg text-slate-300">{fmtTime(winner.duration_seconds)}</span>
        </div>

        <p className="font-serif text-2xl text-[#E5C158] mt-6 reveal-rise" style={{ animationDelay: '1.2s' }}>
          {t('congratulations')} 🥂
        </p>
        {prizeText && (
          <p className="text-sm text-slate-300 mt-3 leading-relaxed px-4 reveal-rise" style={{ animationDelay: '1.5s' }}>
            {prizeText}
          </p>
        )}

        {dismissable && (
          <button
            data-testid="winner-reveal-dismiss"
            onClick={(e) => { e.stopPropagation(); onDismiss?.(); }}
            className="mt-10 inline-flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-slate-400 border border-[#D4AF37]/30 rounded-full px-5 py-2.5 fade-up"
          >
            <X size={14} /> {t('tapToClose')}
          </button>
        )}
      </div>
    </div>
  );
}
